import { Receipt } from 'lucide-react';
import { fmtBRL } from '@/lib/utils';
import type { Lancamento } from './types';

interface LancItemProps {
  item: Lancamento;
  onOpen: (item: Lancamento) => void;
}

/**
 * Row card clicável de lançamento.
 * Touch target ≥ 44px (padding vertical 11px + conteúdo ~22px).
 * Ícone Receipt 38px tile · valor mono tabular · pill status · AIBadge se ocr.
 */
export function LancItem({ item, onOpen }: LancItemProps) {
  return (
    <button
      onClick={() => onOpen(item)}
      aria-label={`Lançamento: ${item.forn}, ${fmtBRL(item.val)}, ${item.status === 'ok' ? 'conciliado' : 'pendente'}`}
      style={{
        textAlign: 'left',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '11px 12px',
        background: 'var(--card)',
        border: '1px solid var(--card-stroke)',
        borderRadius: 'var(--r-3)',
        cursor: 'pointer',
        minHeight: 44,
      }}
    >
      {/* Ícone tile */}
      <span
        style={{
          width: 38,
          height: 38,
          borderRadius: 11,
          background: 'var(--card-soft)',
          display: 'grid',
          placeItems: 'center',
          color: 'var(--fg-muted)',
          flex: '0 0 38px',
        }}
      >
        <Receipt size={17} strokeWidth={1.7} aria-hidden />
      </span>

      {/* Texto principal */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'var(--fg)',
          }}
        >
          {item.forn}
        </div>
        <div
          style={{
            fontSize: 10.5,
            color: 'var(--fg-muted)',
            fontFamily: 'var(--font-mono)',
            marginTop: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span>
            {item.cat} · {item.date}
          </span>
          {item.ocr && (
            <span
              aria-label="Digitalizado via OCR"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                padding: '1px 6px',
                borderRadius: 'var(--r-pill)',
                background: 'var(--lilac-soft, rgba(167,139,250,0.12))',
                color: 'var(--lilac-deep)',
                fontSize: 9,
                fontWeight: 700,
                fontFamily: 'var(--font-mono)',
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: 'var(--lilac-deep)',
                  flexShrink: 0,
                }}
              />
              OCR
            </span>
          )}
        </div>
      </div>

      {/* Valor + status */}
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--fg)',
          }}
        >
          {fmtBRL(item.val)}
        </div>
        <div style={{ marginTop: 3 }}>
          <span
            style={{
              display: 'inline-block',
              padding: '1px 7px',
              borderRadius: 'var(--r-pill)',
              fontSize: 9,
              fontWeight: 700,
              fontFamily: 'var(--font-mono)',
              background:
                item.status === 'ok' ? 'var(--pass-soft)' : 'var(--warn-soft)',
              color: item.status === 'ok' ? 'var(--pass)' : 'var(--warn)',
            }}
          >
            {item.status === 'ok' ? 'conciliado' : 'pendente'}
          </span>
        </div>
      </div>
    </button>
  );
}
